import React from "react";
import { C_MANIGLIA_SILVER, MM, shadeHexColor } from "./constants";

const CHROME = { color: "#b8b8b8", metalness: 0.92, roughness: 0.08 } as const;

// La scena non ha un environment map: con metalness alta il PBR restituisce un
// materiale quasi nero e la maniglieria sparisce sul pannello. Teniamo quindi la
// metalness bassa e la roughness media, cosi' il colore scelto resta leggibile.
const materiali = (colore: string) => ({
  corpo:   { color: colore,                      metalness: 0.45, roughness: 0.30 },
  incavo:  { color: shadeHexColor(colore, -0.16), metalness: 0.35, roughness: 0.45 },
  bordo:   { color: shadeHexColor(colore, -0.30), metalness: 0.35, roughness: 0.40 },
  // profilo scuro dietro la piastrina: stacca la maniglia da qualsiasi colore porta
  contorno: { color: "#2b2b2b", metalness: 0.20, roughness: 0.70 },
});

const LOCK_W      = MM(100);
const LOCK_H      = MM(68);
const LOCK_BORDER = MM(6);
const SQUARE_SIZE = MM(64);

// Barra orizzontale da impugnare
const GrabBar: React.FC<{ y: number; z: number; width: number }> = ({ y, z, width }) => {
  const r      = MM(13);
  const armR   = MM(8);
  const armLen = MM(38);
  const halfW  = width / 2 - MM(20);

  return (
    <group position={[0, y, 0]}>
      {/* Barra orizzontale */}
      <mesh position={[0, 0, z + armLen + r]} rotation={[0, Math.PI / 2, 0]}>
        <cylinderGeometry args={[r, r, width - MM(40), 20]} />
        <meshStandardMaterial {...CHROME} />
      </mesh>
      {/* Capicorda sinistro / destro */}
      <mesh position={[-halfW, 0, z + armLen + r]}>
        <sphereGeometry args={[r, 16, 12]} />
        <meshStandardMaterial {...CHROME} />
      </mesh>
      <mesh position={[halfW, 0, z + armLen + r]}>
        <sphereGeometry args={[r, 16, 12]} />
        <meshStandardMaterial {...CHROME} />
      </mesh>
      {/* Braccio sinistro */}
      <mesh position={[-halfW, 0, z + armLen / 2]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[armR, armR, armLen, 12]} />
        <meshStandardMaterial {...CHROME} />
      </mesh>
      {/* Braccio destro */}
      <mesh position={[halfW, 0, z + armLen / 2]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[armR, armR, armLen, 12]} />
        <meshStandardMaterial {...CHROME} />
      </mesh>
      {/* Piastra di fissaggio sinistra */}
      <mesh position={[-halfW, 0, z + MM(5)]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[MM(16), MM(16), MM(10), 16]} />
        <meshStandardMaterial {...CHROME} />
      </mesh>
      {/* Piastra di fissaggio destra */}
      <mesh position={[halfW, 0, z + MM(5)]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[MM(16), MM(16), MM(10), 16]} />
        <meshStandardMaterial {...CHROME} />
      </mesh>
    </group>
  );
};

// Cilindro serratura con piastrina incavata
const LockPlate: React.FC<{ y: number; z: number; colore: string }> = ({ y, z, colore }) => {
  const pw = LOCK_W, ph = LOCK_H;
  const bw = LOCK_BORDER;
  const fw = pw + bw * 2;      // 112mm
  const fh = ph + bw * 2;      // 80mm
  const depth = MM(20);        // sporgenza della piastrina dal pannello
  const mat = materiali(colore);

  return (
    <group position={[0, y, 0]}>
      {/* Profilo scuro di contorno: rende la piastrina visibile su ogni colore porta */}
      <mesh position={[0, 0, z + MM(1.5)]}>
        <boxGeometry args={[fw + MM(6), fh + MM(6), MM(2)]} />
        <meshStandardMaterial {...mat.contorno} />
      </mesh>
      {/* Bordo superiore */}
      <mesh position={[0, ph / 2 + bw / 2, z + depth / 2]}>
        <boxGeometry args={[fw, bw, depth]} />
        <meshStandardMaterial {...mat.bordo} />
      </mesh>
      {/* Bordo inferiore */}
      <mesh position={[0, -(ph / 2 + bw / 2), z + depth / 2]}>
        <boxGeometry args={[fw, bw, depth]} />
        <meshStandardMaterial {...mat.bordo} />
      </mesh>
      {/* Bordo sinistro */}
      <mesh position={[-(pw / 2 + bw / 2), 0, z + depth / 2]}>
        <boxGeometry args={[bw, ph, depth]} />
        <meshStandardMaterial {...mat.bordo} />
      </mesh>
      {/* Bordo destro */}
      <mesh position={[pw / 2 + bw / 2, 0, z + depth / 2]}>
        <boxGeometry args={[bw, ph, depth]} />
        <meshStandardMaterial {...mat.bordo} />
      </mesh>
      {/* Fondo incavato — arretrato rispetto al bordo */}
      <mesh position={[0, 0, z + MM(4.5)]}>
        <boxGeometry args={[pw, ph, MM(9)]} />
        <meshStandardMaterial {...mat.incavo} />
      </mesh>
      {/* Corpo cilindro serratura */}
      <mesh position={[0, 0, z + MM(16)]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[MM(16), MM(19), MM(26), 20]} />
        <meshStandardMaterial {...mat.corpo} />
      </mesh>
      {/* Buco chiave */}
      <mesh position={[0, MM(6), z + MM(28)]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[MM(6), MM(6), MM(7), 12]} />
        <meshStandardMaterial color="#080808" metalness={0.25} roughness={0.75} />
      </mesh>
    </group>
  );
};

// Piastrina quadrata semplice, senza serratura: un accento visivo sul pannello.
const SimpleSquare: React.FC<{ y: number; z: number; colore: string }> = ({ y, z, colore }) => {
  const size = SQUARE_SIZE;
  const mat  = materiali(colore);

  return (
    <group position={[0, y, 0]}>
      {/* Profilo scuro di contorno: rende la piastrina visibile su ogni colore porta */}
      <mesh position={[0, 0, z + MM(1.5)]}>
        <boxGeometry args={[size + MM(6), size + MM(6), MM(2)]} />
        <meshStandardMaterial {...mat.contorno} />
      </mesh>
      {/* Piastrina quadrata */}
      <mesh position={[0, 0, z + MM(5)]}>
        <boxGeometry args={[size, size, MM(10)]} />
        <meshStandardMaterial {...mat.corpo} />
      </mesh>
    </group>
  );
};

function showsManiglia(tipoManiglia: string | null): boolean {
  return !!tipoManiglia && tipoManiglia !== "Nessuna chiusura" && tipoManiglia !== "SBLOCCO";
}

// Serratura della doppia maniglia. `y` e' gia' la posizione locale (relativa
// al gruppo animato — superiore o inferiore — che contiene il pannello su cui
// la serratura e' montata): va renderizzata dentro quel gruppo, non in un
// gruppo a parte, altrimenti durante l'apertura segue una traiettoria diversa
// da quella del pannello e sembra "fluttuare" staccata dalla porta.
export const ManigliaLock: React.FC<{ y: number; spessore: number; tipoManiglia: string | null; coloreManiglia?: string }> = ({
  y,
  spessore,
  tipoManiglia,
  coloreManiglia,
}) => {
  if (!showsManiglia(tipoManiglia)) return null;
  return <LockPlate y={y} z={spessore / 2} colore={coloreManiglia ?? C_MANIGLIA_SILVER} />;
};

// Piastrina quadrata della doppia maniglia. `y` e' gia' la posizione locale
// nel gruppo animato che contiene il pannello su cui e' montata (vedi ManigliaLock).
export const ManigliaSquare: React.FC<{ y: number; spessore: number; tipoManiglia: string | null; coloreManiglia?: string }> = ({
  y,
  spessore,
  tipoManiglia,
  coloreManiglia,
}) => {
  if (!showsManiglia(tipoManiglia)) return null;
  return <SimpleSquare y={y} z={spessore / 2} colore={coloreManiglia ?? C_MANIGLIA_SILVER} />;
};

// Restituisce true se uno slot finestra/griglia (centro wx, centro assoluto panelWorldY, dimensioni slotW x slotH)
// si sovrappone alla piastrina della serratura o al quadratino, per impedirne il posizionamento nello stesso punto
export function slotOverlapsManiglia(
  wx: number,
  panelWorldY: number,
  slotW: number,
  slotH: number,
  tipoManiglia: string | null | undefined,
  lockY: number | undefined,
  squareY: number | undefined
): boolean {
  if (tipoManiglia !== "DOPPIA") return false;

  const slotLeft   = wx - slotW / 2;
  const slotRight  = wx + slotW / 2;
  const slotBottom = panelWorldY - slotH / 2;
  const slotTop    = panelWorldY + slotH / 2;

  const overlapsCenteredBox = (centerY: number | undefined, halfW: number, halfH: number) => {
    if (centerY === undefined) return false;
    const outerLeft   = -halfW;
    const outerRight  =  halfW;
    const outerBottom = centerY - halfH;
    const outerTop    = centerY + halfH;

    return slotLeft < outerRight && slotRight > outerLeft &&
           slotBottom < outerTop && slotTop > outerBottom;
  };

  const lockOverlap   = overlapsCenteredBox(lockY, (LOCK_W + LOCK_BORDER * 2) / 2, (LOCK_H + LOCK_BORDER * 2) / 2);
  const squareOverlap = overlapsCenteredBox(squareY, (SQUARE_SIZE + MM(6)) / 2, (SQUARE_SIZE + MM(6)) / 2);

  return lockOverlap || squareOverlap;
}
